import type { operations } from "@airange/generated-contracts/edu";
import { eduAdminOpenApiClient } from "../generated/client";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult } from "./openapi-result";

type OperationResponse<
  TOperation extends { responses: Record<number, unknown> },
  TStatus extends keyof TOperation["responses"],
> = TOperation["responses"][TStatus] extends {
  content: { "application/json": infer TResponse };
}
  ? TResponse
  : never;

type OperationRequestBody<TOperation extends { requestBody?: unknown }> = NonNullable<
  TOperation["requestBody"]
> extends {
  content: { "application/json": infer TBody };
}
  ? TBody
  : never;

type OperationQuery<TOperation extends { parameters: { query?: unknown } }> = NonNullable<
  TOperation["parameters"]["query"]
>;

type OperationPath<TOperation extends { parameters: { path?: unknown } }> = NonNullable<
  TOperation["parameters"]["path"]
>;

type UiNonNullQuery<TQuery extends object, TReplaced extends keyof TQuery = never> = {
  [TKey in Exclude<keyof TQuery, TReplaced>]?: NonNullable<TQuery[TKey]>;
};

type GetProjectsOverviewOperation = operations["get_projects_overview_api_projects_overview_get"];
type GetProjectsOperation = operations["get_projects_api_projects_get"];
type CreateProjectOperation = operations["post_project_api_projects_post"];
type GetProjectPaperOptionsOperation = operations["get_project_paper_options_api_projects_paper_options_get"];
type GetProjectOperation = operations["get_project_api_projects__project_id__get"];
type GetProjectExamResultsOperation = operations["get_project_exam_result_list_api_projects__project_id__exam_results_get"];
type GetProjectCourseInstancesOperation =
  operations["get_project_course_instance_list_api_projects__project_id__course_instances_get"];
type GetProjectNoticesOperation = operations["get_project_notice_list_api_projects__project_id__notices_get"];
type CreateProjectNoticeOperation = operations["post_project_notice_api_projects__project_id__notices_post"];
type GetProjectBoardThreadsOperation =
  operations["get_project_board_thread_list_api_projects__project_id__board_threads_get"];
type CreateProjectBoardThreadOperation =
  operations["post_project_board_thread_api_projects__project_id__board_threads_post"];
type BindProjectMembersOperation = operations["put_project_members_api_projects__project_id__members_put"];
type UpdateProjectStepsOperation = operations["put_project_steps_api_projects__project_id__steps_put"];
type ManualReleaseProjectStepOperation =
  operations["post_project_step_manual_release_api_projects__project_id__steps__step_id__manual_release_post"];
type PublishProjectOperation = operations["post_project_publish_api_projects__project_id__publish_post"];
type EndProjectOperation = operations["post_project_end_api_projects__project_id__end_post"];

type GetProjectsGeneratedQuery = OperationQuery<GetProjectsOperation>;
type GetProjectExamResultsGeneratedQuery = OperationQuery<GetProjectExamResultsOperation>;
type GetProjectPaperOptionsQuery = OperationQuery<GetProjectPaperOptionsOperation>;
type ProjectId = OperationPath<GetProjectOperation>["project_id"];
type ProjectStepId = OperationPath<ManualReleaseProjectStepOperation>["step_id"];
type CreateProjectResponse = OperationResponse<CreateProjectOperation, 201>;
type CreateProjectNoticeResponse = OperationResponse<CreateProjectNoticeOperation, 201>;
type CreateProjectBoardThreadResponse = OperationResponse<CreateProjectBoardThreadOperation, 201>;
type BindProjectMembersResponse = OperationResponse<BindProjectMembersOperation, 200>;
type UpdateProjectStepsResponse = OperationResponse<UpdateProjectStepsOperation, 200>;
type ManualReleaseProjectStepResponse = OperationResponse<ManualReleaseProjectStepOperation, 200>;
type PublishProjectResponse = OperationResponse<PublishProjectOperation, 200>;
type EndProjectResponse = OperationResponse<EndProjectOperation, 200>;

export type EduProjectsListQuery = UiNonNullQuery<GetProjectsGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<GetProjectsGeneratedQuery["page_size"]>;
};
export type EduProjectExamResultsQuery = UiNonNullQuery<GetProjectExamResultsGeneratedQuery, "page_size"> & {
  pageSize?: NonNullable<GetProjectExamResultsGeneratedQuery["page_size"]>;
};

export type EduProjectsOverview = OperationResponse<GetProjectsOverviewOperation, 200>;
export type EduProjectOverviewBucket = EduProjectsOverview["buckets"][number];
export type EduProjectsListResponse = OperationResponse<GetProjectsOperation, 200>;
export type EduProjectListItem = EduProjectsListResponse["items"][number];
export type EduProjectLifecycleStatus = EduProjectListItem["lifecycle_status"];
export type EduProjectExecutionStatus = EduProjectListItem["execution_status"];
export type EduProjectCreatePayload = OperationRequestBody<CreateProjectOperation>;
export type EduProjectPaperOptionsResponse = OperationResponse<GetProjectPaperOptionsOperation, 200>;
export type EduProjectPaperOption = EduProjectPaperOptionsResponse["items"][number];
export type EduProjectDetail = OperationResponse<GetProjectOperation, 200>;
export type EduProjectPathStep = EduProjectDetail["steps"][number];
export type EduProjectStepNodeType = EduProjectPathStep["node_type"];
export type EduProjectUnlockTemplate = EduProjectPathStep["unlock_template"];
export type EduProjectMemberGroup = EduProjectDetail["member_groups"][number];
export type EduProjectMemberCard = EduProjectMemberGroup["members"][number];
export type EduProjectMemberStatus = EduProjectMemberCard["status"];
export type EduProjectExamResultsListRead = OperationResponse<GetProjectExamResultsOperation, 200>;
export type EduProjectExamResultRow = EduProjectExamResultsListRead["items"][number];
export type EduProjectCourseInstancesResponse = OperationResponse<GetProjectCourseInstancesOperation, 200>;
export type EduProjectCourseInstanceRow = EduProjectCourseInstancesResponse["items"][number];
export type EduProjectNoticesResponse = OperationResponse<GetProjectNoticesOperation, 200>;
export type EduProjectNoticeItem = EduProjectNoticesResponse["items"][number];
export type EduProjectNoticeSourceKind = EduProjectNoticeItem["source_kind"];
export type EduProjectNoticeCreatePayload = OperationRequestBody<CreateProjectNoticeOperation>;
export type EduProjectBoardThreadsResponse = OperationResponse<GetProjectBoardThreadsOperation, 200>;
export type EduProjectBoardThreadItem = EduProjectBoardThreadsResponse["items"][number];
export type EduProjectBoardThreadCreatePayload = OperationRequestBody<CreateProjectBoardThreadOperation>;
export type EduProjectMemberBindingPayload = OperationRequestBody<BindProjectMembersOperation>;
export type EduProjectStepsUpdatePayload = OperationRequestBody<UpdateProjectStepsOperation>;
export type EduProjectStepUpdateItem = EduProjectStepsUpdatePayload["steps"][number];

function toProjectsListQueryParams(query?: EduProjectsListQuery): GetProjectsGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<GetProjectsGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

function toProjectExamResultsQueryParams(
  query?: EduProjectExamResultsQuery,
): GetProjectExamResultsGeneratedQuery | undefined {
  if (!query) return undefined;
  const { pageSize, ...generatedQuery } = query;
  return omitEmptyQuery<GetProjectExamResultsGeneratedQuery>({
    ...generatedQuery,
    ...(pageSize === undefined ? {} : { page_size: pageSize }),
  });
}

const eduProjectsApi = {
  async getProjectsOverview(): Promise<EduProjectsOverview> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/projects/overview"));
  },
  async getProjects(query?: EduProjectsListQuery): Promise<EduProjectsListResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/projects", {
        params: { query: toProjectsListQueryParams(query) },
      }),
    );
  },
  async getProjectPaperOptions(
    keyword?: NonNullable<GetProjectPaperOptionsQuery["keyword"]>,
  ): Promise<EduProjectPaperOptionsResponse> {
    const normalizedKeyword = keyword === undefined ? undefined : keyword.trim();
    if (normalizedKeyword) {
      return unwrapOpenApiResult(
        await eduAdminOpenApiClient.GET("/api/projects/paper-options", {
          params: { query: { keyword: normalizedKeyword } },
        }),
      );
    }
    return unwrapOpenApiResult(await eduAdminOpenApiClient.GET("/api/projects/paper-options"));
  },
  async createProject(payload: EduProjectCreatePayload): Promise<CreateProjectResponse> {
    return unwrapOpenApiResult(await eduAdminOpenApiClient.POST("/api/projects", { body: payload }));
  },
  async getProjectDetail(projectId: ProjectId): Promise<EduProjectDetail> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/projects/{project_id}", {
        params: { path: { project_id: projectId } },
      }),
    );
  },
  async getProjectCourseInstances(projectId: ProjectId): Promise<EduProjectCourseInstancesResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/projects/{project_id}/course-instances", {
        params: { path: { project_id: projectId } },
      }),
    );
  },
  async getProjectExamResults(
    projectId: ProjectId,
    query?: EduProjectExamResultsQuery,
  ): Promise<EduProjectExamResultsListRead> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/projects/{project_id}/exam-results", {
        params: {
          path: { project_id: projectId },
          query: toProjectExamResultsQueryParams(query),
        },
      }),
    );
  },
  async getProjectNotices(projectId: ProjectId): Promise<EduProjectNoticesResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/projects/{project_id}/notices", {
        params: { path: { project_id: projectId } },
      }),
    );
  },
  async getProjectBoardThreads(projectId: ProjectId): Promise<EduProjectBoardThreadsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.GET("/api/projects/{project_id}/board-threads", {
        params: { path: { project_id: projectId } },
      }),
    );
  },
  async createProjectBoardThread(
    projectId: ProjectId,
    payload: EduProjectBoardThreadCreatePayload,
  ): Promise<CreateProjectBoardThreadResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/projects/{project_id}/board-threads", {
        params: { path: { project_id: projectId } },
        body: payload,
      }),
    );
  },
  async createProjectNotice(
    projectId: ProjectId,
    payload: EduProjectNoticeCreatePayload,
  ): Promise<CreateProjectNoticeResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/projects/{project_id}/notices", {
        params: { path: { project_id: projectId } },
        body: payload,
      }),
    );
  },
  async bindProjectMembers(
    projectId: ProjectId,
    payload: EduProjectMemberBindingPayload,
  ): Promise<BindProjectMembersResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PUT("/api/projects/{project_id}/members", {
        params: { path: { project_id: projectId } },
        body: payload,
      }),
    );
  },
  async updateProjectSteps(
    projectId: ProjectId,
    payload: EduProjectStepsUpdatePayload,
  ): Promise<UpdateProjectStepsResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.PUT("/api/projects/{project_id}/steps", {
        params: { path: { project_id: projectId } },
        body: payload,
      }),
    );
  },
  async manualReleaseProjectStep(
    projectId: ProjectId,
    stepId: ProjectStepId,
  ): Promise<ManualReleaseProjectStepResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/projects/{project_id}/steps/{step_id}/manual-release", {
        params: { path: { project_id: projectId, step_id: stepId } },
      }),
    );
  },
  async publishProject(projectId: ProjectId): Promise<PublishProjectResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/projects/{project_id}/publish", {
        params: { path: { project_id: projectId } },
      }),
    );
  },
  async endProject(projectId: ProjectId): Promise<EndProjectResponse> {
    return unwrapOpenApiResult(
      await eduAdminOpenApiClient.POST("/api/projects/{project_id}/end", {
        params: { path: { project_id: projectId } },
      }),
    );
  },
};

export default eduProjectsApi;
